import { create } from 'zustand'
import type { CatServico, RejeicaoCarreteiro } from '@/core/db/entities'
import { uuidv7 } from '@/lib/uuidv7'
import {
  arred2,
  calcularViagem,
  dec,
  hojeSP,
  validarViagem,
  valorFicha,
  type CampoViagem,
  type CtxRegras,
  type ErroViagem,
  type ResultadoCalculo,
  type SN,
  type TipoFicha,
  type ViagemRegra,
} from './carreteiroRegras'

/**
 * Estado da ficha de carreteiro em construção.
 *
 * UX "Padrão do dia" (análise de 3853 fichas históricas — script
 * `analyze-carreteiro-variance.ts`): dentro de uma ficha, >90% das colunas das
 * viagens repetem. Campos congelados ficam em `FichaDefaults`; cada
 * `ViagemDraft` carrega o delta + cópia dos defaults.
 *
 * Alinhamento com o SIGA web (APP-CARRETEIRO-ALINHAMENTO-SIGA.md):
 *  - NF/concreto é da FICHA (`fichaConcreto`): toda com NF ou toda sem. O SIGA
 *    web lista as viagens filtrando por concreto = ficha_concreto.
 *  - Qde VG é sempre 1 — não existe mais no estado.
 *  - Validação e cálculo vêm de `carreteiroRegras.ts` (cópia do backend).
 *  - `localCargaOrigem` / `localDescargaDeposito` são snapshots do catálogo:
 *    as regras de estaca e de posição dependem deles e precisam rodar offline.
 *  - Data da ficha no fuso de São Paulo (`hojeSP`).
 */

export type TipoCarreteiro = TipoFicha

/** Defaults da ficha — preenchidos no "Padrão do dia" e herdados por cada viagem nova. */
export interface FichaDefaults {
  servico: CatServico | null
  // Origem (cdt_local_carga)
  localCargaId: number | null
  localCargaNome: string | null
  /** local_carga_origem ('P' = pista exige estaca origem no rodoviário) */
  localCargaOrigem: string | null
  // Destino padrão (cdt_local_descarga_pavimento_{urbana|rodoviario})
  localDescargaId: number | null
  localDescargaNome: string | null
  /** depósito do destino ('P' pista no R; 'A'/'R' avenida/rua no U) */
  localDescargaDeposito: string | null
  // Rodoviário: estaca origem padrão
  cargaEstacaOrigem: string | null
  // DT fixo (km) — mesma distância para todas as viagens
  cargaDtFixo: number | null
  // Quantidade padrão (só sem NF — com NF vem do tíquete de cada viagem)
  volume: number | null
}

export interface ViagemDraft {
  id: string // UUID v7
  servico: CatServico
  localCargaId: number | null
  localCargaNome: string | null
  localCargaOrigem: string | null
  cargaChapaApontador: string | null
  cargaHora: string | null
  cargaEstacaOrigem: string | null
  cargaDtFixo: number | null
  localDescargaId: number | null
  localDescargaNome: string | null
  localDescargaDeposito: string | null
  descargaChapaApontador: string | null
  descargaHora: string | null
  // Rodoviário
  descargaEstacaDescarga: string | null
  // Urbana
  descargaBairro: string | null
  descargaRua: string | null
  // Só com NF (ficha concreto)
  descargaLargura: number | null
  descargaComprimento: number | null
  // Só com destino pista (R) ou avenida/rua (U)
  descargaPosicao: string | null
  volume: number | null
  nf: string | null
}

export interface CarreteiroFichaState {
  id: string // UUID v7
  obraId: number | null
  obraNome: string
  projetoId: number | null
  tipo: TipoCarreteiro
  mveId: number | null
  mvePrefixo: string | null
  mvePlaca: string | null
  /**
   * Caminhão de FROTA PRÓPRIA: as viagens entram na linha da Parte Diária do
   * dia (não viram ficha em mvt_carreteiro). Muda só o cabeçalho: exige o nº da
   * ficha de papel e não tem quilometragem (o hodômetro é o da Parte Diária).
   */
  mveProprio?: boolean
  /** Nº da ficha de papel — só frota própria (no terceiro o servidor numera). */
  fichaNumero?: string | null
  /** Chefe do projeto da obra na data. Informativo: o servidor recalcula. */
  chefeEquipeId: number | null
  chefeEquipeNome: string | null
  /** 'live' = confirmado online; 'cache' = último conhecido, sem rede. */
  chefeFonte: 'live' | 'cache' | null
  dt: string // YYYY-MM-DD (São Paulo)
  kmInicial: number | null
  kmFinal: number | null
  chapaEncarregado: string | null
  /** 'S' = ficha com NF/ticket (todas as viagens). */
  fichaConcreto: SN
  /** Padrão do dia — herdado por cada viagem nova. */
  defaults: FichaDefaults
  viagens: ViagemDraft[]
  /** Recusa do servidor, quando a ficha foi reaberta para correção. */
  rejeicao: RejeicaoCarreteiro | null
}

interface CarreteiroStore extends CarreteiroFichaState {
  setObra: (id: number, nome: string, projetoId: number | null, tipo: TipoCarreteiro) => void
  setChefe: (id: number | null, nome: string | null, fonte: 'live' | 'cache' | null) => void
  setMve: (id: number | null, prefixo: string | null, placa: string | null, proprio?: boolean) => void
  setFichaNumero: (v: string | null) => void
  setKmInicial: (v: number | null) => void
  setKmFinal: (v: number | null) => void
  setEncarregadoChapa: (v: string | null) => void
  setDefaults: (patch: Partial<FichaDefaults>) => void
  /** Troca NF da ficha e aplica em todas as viagens (sem NF limpa NF/largura/comprimento). */
  setFichaConcreto: (v: SN) => void
  addOrUpdateViagem: (v: ViagemDraft) => void
  removeViagem: (id: string) => void
  limparRejeicao: () => void
  /** Reseta pra ficha em branco (novo UUID). */
  reset: () => void
  /** Substitui o estado inteiro (draft do Dexie ou ficha recusada reaberta). */
  loadFromState: (state: CarreteiroFichaState) => void
}

function makeDefaults(): FichaDefaults {
  return {
    servico: null,
    localCargaId: null,
    localCargaNome: null,
    localCargaOrigem: null,
    localDescargaId: null,
    localDescargaNome: null,
    localDescargaDeposito: null,
    cargaEstacaOrigem: null,
    cargaDtFixo: null,
    volume: null,
  }
}

function makeInitial(): CarreteiroFichaState {
  return {
    id: uuidv7(),
    obraId: null,
    obraNome: '',
    projetoId: null,
    tipo: 'R',
    mveId: null,
    mvePrefixo: null,
    mvePlaca: null,
    mveProprio: false,
    fichaNumero: null,
    chefeEquipeId: null,
    chefeEquipeNome: null,
    chefeFonte: null,
    dt: hojeSP(),
    kmInicial: null,
    kmFinal: null,
    chapaEncarregado: null,
    fichaConcreto: 'N',
    defaults: makeDefaults(),
    viagens: [],
    rejeicao: null,
  }
}

export const useCarreteiroStore = create<CarreteiroStore>((set) => ({
  ...makeInitial(),

  setObra: (id, nome, projetoId, tipo) =>
    set({
      obraId: id,
      obraNome: nome,
      projetoId,
      tipo,
      chefeEquipeId: null,
      chefeEquipeNome: null,
      chefeFonte: null,
      mveId: null,
      mvePrefixo: null,
      mvePlaca: null,
      mveProprio: false,
      fichaNumero: null,
      defaults: makeDefaults(),
      viagens: [],
    }),
  setChefe: (chefeEquipeId, chefeEquipeNome, chefeFonte) =>
    set({ chefeEquipeId, chefeEquipeNome, chefeFonte }),
  setFichaNumero: (fichaNumero) => set({ fichaNumero }),
  setMve: (mveId, mvePrefixo, mvePlaca, proprio = false) =>
    set({
      mveId,
      mvePrefixo,
      mvePlaca,
      mveProprio: proprio,
      fichaNumero: null,
      // Frota própria não tem km no cabeçalho (o hodômetro é o da Parte Diária).
      ...(proprio ? { kmInicial: null, kmFinal: null } : {}),
      // Trocou de caminhão → invalida serviço/contrato (vigência depende do MVE)
      defaults: makeDefaults(),
      viagens: [],
    }),
  setKmInicial: (kmInicial) => set({ kmInicial }),
  setKmFinal: (kmFinal) => set({ kmFinal }),
  setEncarregadoChapa: (chapaEncarregado) => set({ chapaEncarregado }),

  setDefaults: (patch) => set((s) => ({ defaults: { ...s.defaults, ...patch } })),

  setFichaConcreto: (fichaConcreto) =>
    set((s) => ({
      fichaConcreto,
      viagens:
        fichaConcreto === 'N'
          ? s.viagens.map((v) => ({ ...v, nf: null, descargaLargura: null, descargaComprimento: null }))
          : s.viagens,
    })),

  addOrUpdateViagem: (v) =>
    set((s) => {
      const idx = s.viagens.findIndex((x) => x.id === v.id)
      if (idx >= 0) {
        const next = s.viagens.slice()
        next[idx] = v
        return { viagens: next }
      }
      return { viagens: [...s.viagens, v] }
    }),

  removeViagem: (id) => set((s) => ({ viagens: s.viagens.filter((v) => v.id !== id) })),

  limparRejeicao: () => set({ rejeicao: null }),

  reset: () => set(makeInitial()),

  loadFromState: (state) => set(migrarEstado(state)),
}))

// ============================================================================
//  Migração de rascunhos/outboxes gravados antes do alinhamento com o SIGA
// ============================================================================

type ViagemLegada = Partial<ViagemDraft> & { comNf?: boolean }
type EstadoLegado = Partial<Omit<CarreteiroFichaState, 'defaults' | 'viagens'>> & {
  defaults?: Partial<FichaDefaults> & { comNf?: boolean }
  viagens?: ViagemLegada[]
}

/**
 * Normaliza um estado serializado de qualquer versão: `comNf` (antes por
 * viagem/defaults) vira `fichaConcreto`; campos novos entram como null.
 */
export function migrarEstado(raw: unknown): CarreteiroFichaState {
  const s = (raw ?? {}) as EstadoLegado
  const base = makeInitial()
  const d = s.defaults ?? {}
  const fichaConcreto: SN =
    s.fichaConcreto ?? (d.comNf || (s.viagens ?? []).some((v) => v.comNf) ? 'S' : 'N')

  const viagens: ViagemDraft[] = (s.viagens ?? [])
    .filter((v): v is ViagemLegada & { id: string; servico: CatServico } => !!v.id && !!v.servico)
    .map((v) => ({
      id: v.id,
      servico: v.servico,
      localCargaId: v.localCargaId ?? null,
      localCargaNome: v.localCargaNome ?? null,
      localCargaOrigem: v.localCargaOrigem ?? null,
      cargaChapaApontador: v.cargaChapaApontador ?? null,
      cargaHora: v.cargaHora ?? null,
      cargaEstacaOrigem: v.cargaEstacaOrigem ?? null,
      cargaDtFixo: v.cargaDtFixo ?? null,
      localDescargaId: v.localDescargaId ?? null,
      localDescargaNome: v.localDescargaNome ?? null,
      localDescargaDeposito: v.localDescargaDeposito ?? null,
      descargaChapaApontador: v.descargaChapaApontador ?? null,
      descargaHora: v.descargaHora ?? null,
      descargaEstacaDescarga: v.descargaEstacaDescarga ?? null,
      descargaBairro: v.descargaBairro ?? null,
      descargaRua: v.descargaRua ?? null,
      descargaLargura: fichaConcreto === 'S' ? (v.descargaLargura ?? null) : null,
      descargaComprimento: fichaConcreto === 'S' ? (v.descargaComprimento ?? null) : null,
      descargaPosicao: v.descargaPosicao ?? null,
      volume: v.volume ?? null,
      nf: fichaConcreto === 'S' ? (v.nf ?? null) : null,
    }))

  return {
    id: s.id ?? base.id,
    obraId: s.obraId ?? null,
    obraNome: s.obraNome ?? '',
    projetoId: s.projetoId ?? null,
    tipo: s.tipo ?? 'R',
    mveId: s.mveId ?? null,
    mvePrefixo: s.mvePrefixo ?? null,
    mvePlaca: s.mvePlaca ?? null,
    mveProprio: s.mveProprio === true,
    fichaNumero: s.fichaNumero ?? null,
    chefeEquipeId: s.chefeEquipeId ?? null,
    chefeEquipeNome: s.chefeEquipeNome ?? null,
    chefeFonte: s.chefeFonte ?? (s.chefeEquipeId != null ? 'cache' : null),
    dt: s.dt ?? base.dt,
    kmInicial: s.kmInicial ?? null,
    kmFinal: s.kmFinal ?? null,
    chapaEncarregado: s.chapaEncarregado ?? null,
    fichaConcreto,
    defaults: {
      servico: d.servico ?? null,
      localCargaId: d.localCargaId ?? null,
      localCargaNome: d.localCargaNome ?? null,
      localCargaOrigem: d.localCargaOrigem ?? null,
      localDescargaId: d.localDescargaId ?? null,
      localDescargaNome: d.localDescargaNome ?? null,
      localDescargaDeposito: d.localDescargaDeposito ?? null,
      cargaEstacaOrigem: d.cargaEstacaOrigem ?? null,
      cargaDtFixo: d.cargaDtFixo ?? null,
      volume: d.volume ?? null,
    },
    viagens,
    rejeicao: s.rejeicao ?? null,
  }
}

// ============================================================================
//  Criação de viagens
// ============================================================================

/** Hora atual no formato HH:MM (24h) — pré-preenche carga/descarga. */
export function nowHHMM(): string {
  const d = new Date()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/**
 * Nova viagem a partir do Padrão do dia + última viagem (destino, volume, NF).
 * - Horas = agora; apontadores = chapa do usuário logado (oculto na UI)
 * - Com NF: volume em branco (vem do tíquete); NF = anterior + 1
 * - Posição em branco (só existe com destino pista/avenida/rua)
 */
export function newViagemFromDefaults(
  ficha: CarreteiroFichaState,
  userChapa: string | null,
  last: ViagemDraft | null,
): ViagemDraft {
  const d = ficha.defaults
  if (d.servico == null) {
    throw new Error('Defaults sem serviço — preencha o Padrão do dia antes')
  }
  const now = nowHHMM()
  const comNf = ficha.fichaConcreto === 'S'
  // Destino: herda do padrão, mas se o operador trocou na última viagem, repete
  const destino =
    last?.localDescargaId != null
      ? { id: last.localDescargaId, nome: last.localDescargaNome, deposito: last.localDescargaDeposito }
      : { id: d.localDescargaId, nome: d.localDescargaNome, deposito: d.localDescargaDeposito }
  return {
    id: uuidv7(),
    servico: d.servico,
    localCargaId: d.localCargaId,
    localCargaNome: d.localCargaNome,
    localCargaOrigem: d.localCargaOrigem,
    cargaChapaApontador: userChapa,
    cargaHora: now,
    cargaEstacaOrigem: ficha.tipo === 'R' ? d.cargaEstacaOrigem : null,
    cargaDtFixo: d.cargaDtFixo,
    localDescargaId: destino.id,
    localDescargaNome: destino.nome,
    localDescargaDeposito: destino.deposito,
    descargaChapaApontador: userChapa,
    descargaHora: now,
    descargaEstacaDescarga: null,
    descargaBairro: null,
    descargaRua: null,
    descargaLargura: null,
    descargaComprimento: null,
    descargaPosicao: null,
    volume: comNf ? null : (last?.volume ?? d.volume),
    nf: comNf ? guessNextNf(last?.nf) : null,
  }
}

/**
 * Sugere a próxima NF: número puro ("12345") → +1 mantendo zeros à esquerda.
 * Alfanumérica → repete (operador edita).
 */
function guessNextNf(last: string | null | undefined): string | null {
  if (last == null || last.trim() === '') return null
  const trimmed = last.trim()
  if (/^\d+$/.test(trimmed)) {
    const next = (parseInt(trimmed, 10) + 1).toString()
    return next.padStart(trimmed.length, '0')
  }
  return trimmed
}

/** "Repetir última": clona tudo, com novo UUID, hora = agora e NF + 1. */
export function cloneViagem(src: ViagemDraft, userChapa: string | null, fichaConcreto: SN): ViagemDraft {
  const now = nowHHMM()
  return {
    ...src,
    id: uuidv7(),
    cargaHora: now,
    descargaHora: now,
    cargaChapaApontador: userChapa,
    descargaChapaApontador: userChapa,
    nf: fichaConcreto === 'S' ? guessNextNf(src.nf) : null,
  }
}

// ============================================================================
//  Regras (validação e cálculo) sobre o estado da ficha
// ============================================================================

/** ViagemDraft → campos das regras. Qde VG fixa em 1; concreto herdado da ficha. */
export function paraRegra(v: ViagemDraft, fichaConcreto: SN): ViagemRegra {
  return {
    servicoExecutadoId: v.servico.id,
    localCargaId: v.localCargaId,
    localDescargaId: v.localDescargaId,
    cargaHora: v.cargaHora,
    descargaHora: v.descargaHora,
    cargaDtFixo: v.cargaDtFixo,
    cargaEstacaOrigem: v.cargaEstacaOrigem,
    descargaEstacaDescarga: v.descargaEstacaDescarga,
    cargaChapaApontador: v.cargaChapaApontador,
    descargaChapaApontador: v.descargaChapaApontador,
    descargaBairro: v.descargaBairro,
    descargaRua: v.descargaRua,
    descargaLargura: v.descargaLargura,
    descargaComprimento: v.descargaComprimento,
    descargaPosicao: v.descargaPosicao,
    volume: v.volume,
    nf: v.nf,
    concreto: fichaConcreto,
    qdeVg: 1,
  }
}

/** Catálogos das regras a partir dos snapshots da viagem (funciona offline). */
export function ctxDaViagem(
  v: Pick<ViagemDraft, 'servico' | 'localCargaOrigem' | 'localDescargaDeposito'>,
): CtxRegras {
  return {
    servico: () => ({ nome: v.servico.nome, inativo: v.servico.inativo ?? false }),
    localCarga: () => ({ origem: v.localCargaOrigem }),
    localDescarga: () => ({ deposito: v.localDescargaDeposito }),
  }
}

export function errosDaViagem(ficha: CarreteiroFichaState, v: ViagemDraft): ErroViagem[] {
  return validarViagem(ficha.tipo, paraRegra(v, ficha.fichaConcreto), ctxDaViagem(v))
}

export interface ErrosViagemFicha {
  viagemId: string
  indice: number
  erros: ErroViagem[]
}

/** Erros de todas as viagens (só as que têm erro). */
export function errosDaFicha(ficha: CarreteiroFichaState): ErrosViagemFicha[] {
  return ficha.viagens
    .map((v, indice) => ({ viagemId: v.id, indice, erros: errosDaViagem(ficha, v) }))
    .filter((e) => e.erros.length > 0)
}

// Campos que só existem viagem a viagem — não travam o Padrão do dia.
const CAMPOS_DA_VIAGEM: CampoViagem[] = ['cargaHora', 'descargaHora', 'volume', 'estacaDescarga']

/**
 * Regras do Padrão do dia: `validarViagem` sobre uma viagem-modelo, ignorando
 * horas, volume e estaca de descarga (digitados em cada viagem).
 */
export function errosDoPadrao(ficha: CarreteiroFichaState): ErroViagem[] {
  const d = ficha.defaults
  const modelo: ViagemRegra = {
    servicoExecutadoId: d.servico?.id ?? null,
    localCargaId: d.localCargaId,
    localDescargaId: d.localDescargaId,
    cargaHora: null,
    descargaHora: null,
    cargaDtFixo: d.cargaDtFixo,
    cargaEstacaOrigem: ficha.tipo === 'R' ? d.cargaEstacaOrigem : null,
    descargaEstacaDescarga: null,
    cargaChapaApontador: null,
    descargaChapaApontador: null,
    descargaBairro: null,
    descargaRua: null,
    descargaLargura: null,
    descargaComprimento: null,
    descargaPosicao: null,
    volume: null,
    nf: null,
    concreto: ficha.fichaConcreto,
    qdeVg: 1,
  }
  const ctx: CtxRegras = {
    servico: () => (d.servico ? { nome: d.servico.nome, inativo: d.servico.inativo ?? false } : null),
    localCarga: () => ({ origem: d.localCargaOrigem }),
    localDescarga: () => ({ deposito: d.localDescargaDeposito }),
  }
  return validarViagem(ficha.tipo, modelo, ctx).filter((e) => !CAMPOS_DA_VIAGEM.includes(e.campo))
}

/** Nº da ficha de papel da frota própria: só dígitos, até 10 (mve_ficha char(10)). */
export function fichaNumeroValido(v: string | null | undefined): boolean {
  return /^\d{1,10}$/.test((v ?? '').trim())
}

export function isHeaderComplete(s: CarreteiroFichaState): boolean {
  if (s.obraId == null || s.mveId == null) return false
  // Frota própria não pede mais o nº da ficha: o servidor numera (mesma série do terceiro).
  return true
}

/** Padrão do dia completo? Bloqueia o "+ Adicionar viagem" até estar. */
export function isDefaultsComplete(s: CarreteiroFichaState): boolean {
  return errosDoPadrao(s).length === 0
}

/**
 * Cálculo do SIGA sobre a viagem (prévia — o backend recalcula no sync com o
 * item de contrato vigente). Garantia e preço vêm do snapshot do serviço.
 */
export function calculoViagem(ficha: CarreteiroFichaState, v: ViagemDraft): ResultadoCalculo {
  return calcularViagem(ficha.tipo, paraRegra(v, ficha.fichaConcreto), {
    garantia: v.servico.contratoGarantia ?? null,
    preco: v.servico.contratoPreco ?? null,
  })
}

export function viagemProducao(ficha: CarreteiroFichaState, v: ViagemDraft): number {
  return arred2(calculoViagem(ficha, v).producao)
}

export function totalProducao(s: CarreteiroFichaState): number {
  return arred2(s.viagens.reduce((acc, v) => acc + calculoViagem(s, v).producao, 0))
}

export function totalVolume(s: CarreteiroFichaState): number {
  return arred2(s.viagens.reduce((acc, v) => acc + dec(v.volume), 0))
}

/** Valor estimado da ficha (Σ VPgCar, arredonda só o total). null sem preço em cache. */
export function totalValor(s: CarreteiroFichaState): number | null {
  if (s.viagens.some((v) => v.servico.contratoPreco == null)) return null
  return valorFicha(s.viagens.map((v) => calculoViagem(s, v)))
}
